import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Chip,
  FormControl, InputLabel, Select, MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(() => ({
  slotsGrid: {
    display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, marginBottom: 12,
  },
  slot: {
    cursor: "pointer", fontWeight: 600, fontSize: 13,
    "&:hover": { transform: "scale(1.05)" },
  },
}));

const Schema = Yup.object().shape({
  doctorId: Yup.number().required(i18n.t("appointmentModal.validation.doctorRequired")),
  patientName: Yup.string().required(i18n.t("appointmentModal.validation.nameRequired")),
  date: Yup.string().required(i18n.t("appointmentModal.validation.dateRequired")),
  startTime: Yup.string().required(i18n.t("appointmentModal.validation.timeRequired")),
});

const statusColors = { pending: "#f59e0b", confirmed: "#22c55e", completed: "#3b82f6", cancelled: "#ef4444", no_show: "#6b7280" };

const AppointmentModal = ({ open, onClose, appointment, doctors, onSave }) => {
  const classes = useStyles();
  const statusLabels = {
    pending: i18n.t("appointmentModal.status.pending"),
    confirmed: i18n.t("appointmentModal.status.confirmed"),
    completed: i18n.t("appointmentModal.status.completed"),
    cancelled: i18n.t("appointmentModal.status.cancelled"),
    no_show: i18n.t("appointmentModal.status.noShow"),
  };
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const isEditing = !!appointment;

  const initialValues = {
    doctorId: appointment?.doctorId || "",
    patientName: appointment?.patientName || "",
    patientPhone: appointment?.patientPhone || "",
    date: appointment?.date || "",
    startTime: appointment?.startTime || "",
    service: appointment?.service || "",
    notes: appointment?.notes || "",
  };

  const loadSlots = async (doctorId, date) => {
    if (!doctorId || !date) { setSlots([]); return; }
    setLoadingSlots(true);
    try {
      const { data } = await api.get(`/clinic/slots/${doctorId}/${date}`);
      setSlots(data.slots || []);
    } catch (err) {
      setSlots([]);
      toastError(err);
    }
    setLoadingSlots(false);
  };

  const handleSave = async (values) => {
    try {
      if (isEditing) {
        // Reschedule se data/hora mudou
        if (values.date !== appointment.date || values.startTime !== appointment.startTime) {
          await api.put(`/clinic/appointments/${appointment.id}/reschedule`, { date: values.date, startTime: values.startTime });
        }
      } else {
        await api.post("/clinic/appointments", values);
      }
      toast.success(isEditing ? i18n.t("appointmentModal.toasts.updated") : i18n.t("appointmentModal.toasts.created"));
      if (onSave) onSave();
      onClose();
    } catch (err) { toastError(err); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? i18n.t("appointmentModal.title.edit") : i18n.t("appointmentModal.title.add")}</DialogTitle>
      <Formik initialValues={initialValues} enableReinitialize validationSchema={Schema} onSubmit={handleSave}>
        {({ values, setFieldValue, touched, errors, isSubmitting }) => {
          // Load slots when doctor+date change
          useEffect(() => {
            loadSlots(values.doctorId, values.date);
          }, [values.doctorId, values.date]);

          return (
            <Form>
              <DialogContent dividers>
                {isEditing && appointment?.status && (
                  <div style={{ marginBottom: 12 }}>
                    <Chip label={statusLabels[appointment.status]} style={{ backgroundColor: statusColors[appointment.status], color: "#fff" }} />
                  </div>
                )}

                <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 12 }}>
                  <InputLabel>{i18n.t("appointmentModal.form.doctor")}</InputLabel>
                  <Select value={values.doctorId} onChange={e => { setFieldValue("doctorId", e.target.value); setFieldValue("startTime", ""); }} label={i18n.t("appointmentModal.form.doctor")} error={touched.doctorId && !!errors.doctorId}>
                    {doctors.map(d => <MenuItem key={d.id} value={d.id}>{d.name} - {d.specialization}</MenuItem>)}
                  </Select>
                </FormControl>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <Field as={TextField} name="patientName" label={i18n.t("appointmentModal.form.patientName")} variant="outlined" size="small" fullWidth error={touched.patientName && !!errors.patientName} helperText={touched.patientName && errors.patientName} />
                  <Field as={TextField} name="patientPhone" label={i18n.t("appointmentModal.form.phone")} variant="outlined" size="small" fullWidth />
                </div>

                <TextField
                  type="date" variant="outlined" size="small" fullWidth label={i18n.t("appointmentModal.form.date")} InputLabelProps={{ shrink: true }}
                  value={values.date} onChange={e => { setFieldValue("date", e.target.value); setFieldValue("startTime", ""); }}
                  error={touched.date && !!errors.date} helperText={touched.date && errors.date}
                  style={{ marginBottom: 8 }}
                />

                {/* Slot picker */}
                <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>
                  {loadingSlots ? i18n.t("appointmentModal.slots.loading") : values.doctorId && values.date ? `${i18n.t("appointmentModal.slots.available")} (${slots.length}):` : i18n.t("appointmentModal.slots.selectDoctorDate")}
                </div>
                <div className={classes.slotsGrid}>
                  {slots.map(s => (
                    <Chip
                      key={s}
                      label={s}
                      className={classes.slot}
                      onClick={() => setFieldValue("startTime", s)}
                      color={values.startTime === s ? "primary" : "default"}
                      variant={values.startTime === s ? "default" : "outlined"}
                    />
                  ))}
                  {!loadingSlots && values.doctorId && values.date && slots.length === 0 && (
                    <div style={{ color: "#ef4444", fontSize: 12 }}>{i18n.t("appointmentModal.slots.noSlots")}</div>
                  )}
                </div>
                {touched.startTime && errors.startTime && <div style={{ color: "#ef4444", fontSize: 12 }}>{errors.startTime}</div>}

                <Field as={TextField} name="service" label={i18n.t("appointmentModal.form.service")} variant="outlined" size="small" fullWidth style={{ marginBottom: 12, marginTop: 8 }} />
                <Field as={TextField} name="notes" label={i18n.t("appointmentModal.form.notes")} variant="outlined" size="small" fullWidth multiline minRows={2} />
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose}>{i18n.t("appointmentModal.buttons.cancel")}</Button>
                <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                  {isEditing ? i18n.t("appointmentModal.buttons.save") : i18n.t("appointmentModal.buttons.schedule")}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default AppointmentModal;
